import { useCallback, useEffect, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router";
import { DialogsProvider } from "@toolpad/core";
import { OpenDialogsContext } from "./OpenDialogsContext";
import type { OpenDialogsContextValue } from "./OpenDialogsContext";

type Props = {
  children?: ReactNode;
};

const isDialogDismissalState = (
  state: unknown
): state is { dismissDialogs: true } =>
  typeof state === "object" &&
  state !== null &&
  "dismissDialogs" in state &&
  state.dismissDialogs === true;

const OpenDialogsProvider = (props: Props) => {
  const { children } = props;
  const openDialogs = useRef(new Map<Promise<unknown>, () => void>());
  const location = useLocation();
  const pathname = location.pathname;
  const navigationState: unknown = location.state;
  const dismissedFor = useRef(pathname);

  const track = useCallback<OpenDialogsContextValue["track"]>(
    (handle, dismiss) => {
      openDialogs.current.set(handle, dismiss);
      const forget = () => {
        openDialogs.current.delete(handle);
      };
      void handle.then(forget, forget);
    },
    []
  );

  const dismissAll = useCallback(() => {
    const dismissals = [...openDialogs.current.values()];
    openDialogs.current.clear();
    dismissals.forEach((dismiss) => {
      dismiss();
    });
  }, []);

  useEffect(() => {
    const pathChanged = dismissedFor.current !== pathname;
    dismissedFor.current = pathname;
    if (pathChanged || isDialogDismissalState(navigationState)) {
      dismissAll();
    }
  }, [pathname, navigationState, dismissAll]);

  const value = useMemo(() => ({ track, dismissAll }), [track, dismissAll]);

  return (
    <OpenDialogsContext.Provider value={value}>
      {children}
    </OpenDialogsContext.Provider>
  );
};

/**
 * Toolpad renders its dialog stack from the provider, which sits above the
 * routed outlet. A router navigation therefore swaps the page underneath an
 * open dialog without unmounting it — a link inside a modal changes the route
 * and leaves the modal on screen. Pairing the stack with a registry of open
 * dialogs lets `useTrackedDialogs` dismiss them when the path changes.
 *
 * Dialogs opened outside this provider (e.g. tests rendering Toolpad's
 * `DialogsProvider` directly) are simply untracked.
 */
export const AppDialogsProvider = (props: Props) => {
  const { children } = props;

  return (
    <OpenDialogsProvider>
      <DialogsProvider>{children}</DialogsProvider>
    </OpenDialogsProvider>
  );
};
